import { WhichWeightAPI } from "WhichWeightAPI"
import { useState, useRef } from "react"
import { useSelector, useDispatch } from "react-redux"
import { updateOrganizationDetails } from 'redux/slices/user'
import { toBase64 } from 'functions/encoding'
import { XMarkIcon } from '@heroicons/react/20/solid'
import kinesisLogo from "media/kinesis_logo.png"

import ScreenFrame from "components/Frame/ScreenFrame"
import ContentFrame from "components/Frame/ContentFrame"
import BrandColorButton from "components/Buttons/BrandColorButton"

export default function OrganizationSettingsTab() {

    const dispatch = useDispatch()

    const organizationLogoInputRef = useRef(null)

    const organizationId = useSelector((state) => state.user.organizationId)
    const organizationName = useSelector((state) => state.user.organizationName)
    const organizationWebsite = useSelector((state) => state.user.organizationWebsite)
    const organizationLogo = useSelector((state) => state.user.organizationLogo)

    const [editedOrganizationName, setEditedOrganizationName] = useState(organizationName || '')
    const [editedOrganizationWebsite, setEditedOrganizationWebsite] = useState(organizationWebsite || '')
    const [editedOrganizationLogoPreview, setEditedOrganizationLogoPreview] = useState(organizationLogo || null)
    const [orgLogoFile, setOrgLogoFile] = useState(null)
    const [orgLogoChanged, setOrgLogoChanged] = useState(false)
    const [orgLogoRemoved, setOrgLogoRemoved] = useState(false)
    const [isDragging, setIsDragging] = useState(false)
    const [orgNameValid, setOrgNameValid] = useState(true)
    const [isSavingOrganization, setIsSavingOrganization] = useState(false)

    const HandleOrganizationLogoChange = (file) => {
        if (file) {
            // Validate file type
            if (!file.type.match(/^image\/(png|jpeg|jpg)$/)) {
                console.warn('Invalid file type. Please select PNG, JPG, or JPEG')
                return
            }
            
            setOrgLogoFile(file)
            setOrgLogoChanged(true)
            setOrgLogoRemoved(false)
            try {
                const img = new Image()
                img.src = URL.createObjectURL(file)
                img.onload = () => { 
                    setEditedOrganizationLogoPreview(img.src)
                }
            } catch (error) {
                console.warn('Failed to load image preview', error)
            }
        } else {
            // Clear file selection
            setOrgLogoFile(null)
            if (organizationLogo) {
                // If there was an existing logo, mark it for removal
                setOrgLogoRemoved(true)
                setOrgLogoChanged(true)
                setEditedOrganizationLogoPreview(null)
            } else {
                // No existing logo, just reset
                setOrgLogoRemoved(false)
            setOrgLogoChanged(false)
                setEditedOrganizationLogoPreview(null)
            }
            if (organizationLogoInputRef.current) {
                organizationLogoInputRef.current.value = ''
            }
        }
    }

    const HasUnsavedChanges = () => {
        return (
            orgLogoChanged 
            || editedOrganizationName.trim() !== (organizationName || '').trim() 
            || editedOrganizationWebsite.trim() !== (organizationWebsite || '').trim()
        )
    }

    const SaveOrganizationDetails = async () => {
        if (editedOrganizationName.trim().length === 0) {
            setOrgNameValid(false)
            return
        }

        setOrgNameValid(true)
        setIsSavingOrganization(true)

        try {
            const jsonBody = {
                organizationId: organizationId,
                organizationName: editedOrganizationName.trim(),
                organizationWebsite: editedOrganizationWebsite.trim() || null
            }

            // Handle logo: null, existing URL, or bytes
            if (orgLogoRemoved) {
                jsonBody.organizationLogo = null
            } else if (orgLogoFile) {
                const encodedLogo = await toBase64(orgLogoFile)
                jsonBody.organizationLogo = encodedLogo
            } else {
                jsonBody.organizationLogo = organizationLogo || null
            }

            const response = await WhichWeightAPI("REPORTING", "PATCH", "/organization_details_update", jsonBody)

            if (response.data.status === 'success') {
                const updatePayload = {
                    organizationName: editedOrganizationName.trim(),
                    organizationWebsite: editedOrganizationWebsite.trim() || ''
                }
                
                if (orgLogoRemoved) {
                    updatePayload.organizationLogo = null
                } else if (orgLogoFile && editedOrganizationLogoPreview) {
                    updatePayload.organizationLogo = editedOrganizationLogoPreview
                }
                
                dispatch(updateOrganizationDetails(updatePayload))
                
                setOrgLogoChanged(false)
                setOrgLogoFile(null)
                setOrgLogoRemoved(false)
            } else {
                console.error('Failed to update organization details:', response.data)
            }
        } catch (error) {
            console.error('Error updating organization details:', error)
        } finally {
            setIsSavingOrganization(false)
        }
    }

    return (
        <ScreenFrame>
            <div className='max-w-4xl mx-auto space-y-4'>
                <div className='flex items-center space-y-1 gap-x-2 pb-2'>
                    <h1 className="text-2xl font-semibold text-gray-900 tracking-tight pt-2 flex-grow">
                        Organization Info
                    </h1>
                </div>
            
                <ContentFrame>
                    <div className="w-full p-6 space-y-6">
                        <div className="col-span-2">
                            <label htmlFor="organizationName" className="block text-sm font-medium leading-6 text-gray-900">
                                Organization Name*
                            </label>
                            <div className="mt-2">
                                <input
                                    id="organizationName"
                                    name="organizationName"
                                    type="organizationName"
                                    placeholder="Enter organization name here"
                                    autoComplete="organizationName"
                                    className="block w-full rounded-lg border-0 px-3 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6 focus:outline-orange-400"
                                    value={editedOrganizationName}
                                    onChange={(e) => {
                                        setEditedOrganizationName(e.target.value)
                                        setOrgNameValid(true)
                                    }}
                                    maxLength={255}
                                    disabled={true}
                                />
                            </div>
                        </div>

                        {editedOrganizationWebsite.trim().length > 0 && (
                            <div className="col-span-2">
                                <label htmlFor="organizationWebsite" className="block text-sm font-medium leading-6 text-gray-900">
                                    Organization Website
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="organizationWebsite"
                                        name="organizationWebsite"
                                        type="url"
                                        autoComplete="url"
                                        placeholder="https://www.example.com"
                                        className="block w-full rounded-lg border-0 px-3 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6 focus:outline-orange-400"
                                        value={editedOrganizationWebsite}
                                        onChange={(e) => setEditedOrganizationWebsite(e.target.value)}
                                        maxLength={255}
                                        disabled={true}
                                    />
                                </div>
                            </div>
                        )}

                        <div className='col-span-2'>
                            <label htmlFor="photo" className="block text-sm font-medium leading-6 text-gray-900 mb-2">
                                Organization Logo
                            </label>

                            {/* <div className='text-xs text-gray-500 mb-2'>Recommended 256x256 PNG file format</div> */}

                            <div className='flex items-center gap-x-4'>
                                {editedOrganizationLogoPreview === null &&
                                    <div>
                                        <div className='w-20 h-20 border border-gray-200 rounded-full overflow-hidden'>
                                            <img
                                                src={kinesisLogo}
                                                alt="Default organization logo"
                                                className='w-full h-full object-cover'
                                            />
                                        </div>
                                    </div>
                                }

                                {editedOrganizationLogoPreview !== null &&
                                    <div>
                                        <div className='w-20 h-20 border border-gray-200 rounded-full overflow-hidden'>
                                            <img
                                                src={editedOrganizationLogoPreview}
                                                alt="Selected file preview"
                                                className='w-full h-full object-cover'
                                            />
                                        </div>
                                    </div>
                                }

                                {/* <div className='w-1/2 p-2 bg-gray-50 rounded-lg'>
                                    <input
                                        ref={organizationLogoInputRef}
                                        className='pr-2 w-full text-sm text-gray-900 cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-orange-400 file:text-white file:cursor-pointer'
                                        type="file"
                                        accept=".png,.jpg,.jpeg"
                                        // onChange={(event) => HandleFormChange('organizationLogo', event.target.files[0])}
                                        onChange={(e) => HandleOrganizationLogoChange(e.target.files[0])}
                                    />
                                </div>

                                {editedOrganizationLogoPreview !== null &&
                                    <div
                                        className='w-6 h-6 flex items-center justify-center cursor-pointer'
                                        // onClick={() => HandleFormChange('organizationLogo', null)}
                                        onClick={() => HandleOrganizationLogoChange(null)}
                                    >
                                        <XMarkIcon className='w-6 h-6 text-gray-400' />
                                    </div>
                                } */}
                            </div>
                        </div>
                    </div>
                </ContentFrame>

                <p className='pt-1 text-center text-gray-600 text-sm'>
                    To edit organization details, contact us at <a href="mailto:support@kinesisintegrated.com" className="text-blue-500 underline text-sm">support@kinesisintegrated.com</a>
                </p>

                {HasUnsavedChanges() && (
                    <div className="pt-2 w-full flex items-center justify-end">
                        <div className="w-1/3">
                            <BrandColorButton
                                text="Save Changes"
                                action={() => SaveOrganizationDetails()}
                                isLoading={isSavingOrganization}
                                className="shadow-md"
                            />
                        </div>
                    </div>
                )}
            </div>
        </ScreenFrame>
    )
}